import { ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from './askAiChat.module.scss';

interface IMarkdownRendererProps {
  content: string,
  exportButton?: ReactNode,
  renderTableActions?: (tableLine: number) => ReactNode,
}

const EXCEL_HINT = 'Download the Excel';

const components: Components = {
  h1: ({ children }) => <h1 className={ styles.markdownHeading }>{ children }</h1>,
  h2: ({ children }) => <h2 className={ styles.markdownHeading }>{ children }</h2>,
  h3: ({ children }) => <h3 className={ styles.markdownHeading }>{ children }</h3>,
  a: ({ children, href }) => (
    <a className={ styles.markdownLink } href={ href } target="_blank" rel="noopener noreferrer">
      { children }
    </a>
  ),
  ul: ({ children }) => <ul className={ styles.markdownList }>{ children }</ul>,
  ol: ({ children }) => <ol className={ styles.markdownList }>{ children }</ol>,
  li: ({ children }) => <li className={ styles.markdownListItem }>{ children }</li>,
  th: ({ children }) => <th className={ styles.markdownTh }>{ children }</th>,
  td: ({ children }) => <td className={ styles.markdownTd }>{ children }</td>,
  code: ({ children }) => <code className={ styles.markdownCode }>{ children }</code>,
};

const MarkdownRenderer = ({ content, exportButton, renderTableActions }: IMarkdownRendererProps) => {
  const table: Components['table'] = ({ children, node }) => (
    <div className={ styles.markdownTableBlock }>
      <div className={ styles.markdownTableWrp }>
        <table className={ styles.markdownTable }>{ children }</table>
      </div>
      { renderTableActions?.(node?.position?.start.line ?? 0) }
    </div>
  );

  const em: Components['em'] = ({ children }) => {
    if (!exportButton || typeof children !== 'string' || !children.includes(EXCEL_HINT)) {
      return <em>{ children }</em>;
    }

    const hintStart = children.indexOf(EXCEL_HINT);

    return (
      <em>
        { children.slice(0, hintStart) }
        { exportButton }
        { children.slice(hintStart) }
      </em>
    );
  };

  return (
    <ReactMarkdown remarkPlugins={ [remarkGfm] } components={ { ...components, table, em } }>
      { content }
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
